import { createRouter, createWebHashHistory, type Router, type RouteRecordRaw } from 'vue-router';
import { useLocalization, DEFAULT_LANGUAGE, AVAILABLE_LANGUAGES } from './i18n';
import { useScroll } from '@vueuse/core';

const OverviewComponent = () => import('./views/overview/OverviewView.vue');
const DetailsComponent = () => import('./views/details/DetailsView.vue');
const EventsOverallComponent = () =>
  import('./components/details/events-overall/EventsOverallComponent.vue');
const EventsByPortsComponent = () =>
  import('./components/details/events-by-ports/EventsByPortsComponent.vue');
const EventsByUrlsComponent = () =>
  import('./components/details/events-by-urls/EventsByUrlsComponent.vue');
const ImprintComponent = () => import('./views/imprint/ImprintView.vue');
const PrivacyComponent = () => import('./views/privacy/PrivacyView.vue');
const DisclaimerComponent = () => import('./views/disclaimer/DisclaimerView.vue');
const AboutView = () => import('./views/about/AboutView.vue');

export enum RouteNames {
  details = 'details',
  detailsPorts = 'details-ports',
  detailsUrls = 'details-urls',
  detailsOverall = 'details-overall',
}

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/' + DEFAULT_LANGUAGE,
  },

  {
    path: '/:lang',
    beforeEnter: (to) => {
      const theLang = to.params['lang']
        ? typeof to.params['lang'] === 'string'
          ? to.params['lang']
          : to.params['lang'][0]
        : '';
      if (!to.params['lang'] || !AVAILABLE_LANGUAGES.includes(theLang as any)) {
        return { params: { lang: DEFAULT_LANGUAGE } };
      }
      return true;
    },
    redirect: (to) => {
      return { name: 'tacho', params: { lang: to.params.lang } };
    },
    children: [
      {
        path: 'tacho',
        name: 'tacho',
        component: OverviewComponent,
      },
      {
        path: 'details',
        name: RouteNames.details,
        components: {
          default: DetailsComponent,
          details: EventsOverallComponent,
        },
        redirect: { name: 'details-overall' },
        children: [
          {
            path: 'overall',
            name: 'details-overall',
            components: {
              default: DetailsComponent,
              details: EventsOverallComponent,
            },
          },
          {
            path: 'ports',
            name: RouteNames.detailsPorts,
            components: {
              default: DetailsComponent,
              details: EventsByPortsComponent,
            },
            children: [
              {
                name: 'by-port',
                path: ':port',
                component: EventsByPortsComponent,
                props: (route) => ({
                  detailParam: route.params.port,
                }),
              },
            ],
          },
          {
            path: 'urls',
            name: RouteNames.detailsUrls,
            components: {
              default: DetailsComponent,
              details: EventsByUrlsComponent,
            },
            children: [
              {
                name: 'by-url',
                path: ':url',
                component: EventsByPortsComponent,
                props: (route) => ({
                  detailParam: route.params.url,
                }),
              },
            ],
          },
        ],
      },
      {
        path: 'imprint',
        name: 'imprint',
        component: ImprintComponent,
      },
      {
        path: 'privacy',
        name: 'privacy',
        component: PrivacyComponent,
      },
      {
        path: 'disclaimer',
        name: 'disclaimer',
        component: DisclaimerComponent,
      },
      {
        path: 'about',
        name: 'about',
        component: AboutView,
      },
    ],
  },
];
const { x, y } = useScroll(window, { behavior: 'smooth' });
function setupRouter() {
  router = createRouter({
    history: createWebHashHistory(),
    routes,
  });
  router.beforeResolve(async (to) => {
    if (!to.params['lang']) {
      return true;
    }
    const theLang = to.params['lang']
      ? typeof to.params['lang'] === 'string'
        ? to.params['lang']
        : to.params['lang'][0]
      : '';
    const { i18n, switchLanguage } = useLocalization();
    if (theLang !== i18n.global.locale.value) {
      switchLanguage(theLang);
    }
  });
  router.afterEach(() => {
    x.value = 0;
    y.value = 0;
  });
}
let router: Router;
export function useRouter() {
  if (!router) {
    setupRouter();
  }

  return { router };
}
